import React from 'react';
import { customIconsDictionary } from './Icons';
import { generateComponentDocs, generateStoryDocs } from '../../utils/documentationUtils';

export default {
  title: 'Components/Icons/Icons',
  parameters: {
    layout: 'centered',
    ...generateComponentDocs(
      // What problem does it solve?
      '\nThe `Icons` component provides a collection of reusable, customizable icons for various UI elements. It allows for dynamic size, color, and additional styling with class names.\n\n',
      // When & how to use it?
      `\nUse the \`Icons\` component to enhance the user interface by providing visual context for actions, statuses, and navigation. These icons are versatile and can be used in:
      \n- Buttons
      \n- Lists
      \n- Navigation
      \n- Status indicators etc.\n\n`,
      // Current Usage
      `\nThe \`Icons\` component is used across the application in:
      \n- Buttons (e.g., \`Add\`, \`Delete\`)
      \n- Action indicators (e.g., \`Completed\`, \`Not Completed\`)
      \n- Navigation links etc.\n\n`,
      // Related Components
      `\n- **[Button](http://localhost:6006/?path=/docs/components-button--docs)**: Frequently paired with icons for enhanced functionality.
      \n- **[EmptyState](http://localhost:6006/?path=/docs/components-emptystate--docs)**: Uses icons to illustrate the state of empty pages.\n\n`,
    ),
  },
  tags: ['autodocs'],
  argTypes: {
    Component: {
      control: false,
      description: 'The icon component to render. It must be one of the components from `customIconsDictionary`.',
      table: {
        type: { summary: 'React.ComponentType' },
      },
    },
    label: {
      control: { type: 'text' },
      description: 'The label to display below the icon.',
      table: {
        type: { summary: 'string' },
      },
    },
  },
};

export const AllIcons = () => {
  const renderTemplate = ({ Component, label }) => (
    <div key={label} className="flex flex-col items-center gap-2">
      <Component />
      <p className="text-sm">{label}</p>
    </div>
  );

  return (
    <div className="grid grid-cols-4 gap-6">
      {customIconsDictionary.map(renderTemplate)}
    </div>
  );
};

AllIcons.parameters = generateStoryDocs(
  // When is this variant used?
  '\nThe `AllIcons` variant is used for viewing and testing all available icons. It provides a complete showcase of the icons with their respective labels.\n\n',
  // Where is it currently used on the product?
  '\nThis variant is used in Storybook for development purposes to review the available icons and their styling.\n\n',
);
